import { useEffect, useId, useMemo, useRef, useState, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { hostError, hostsApi, type Host, type HostCommand, type InstallInfo, type NodeInfo } from '../../api'
import { t } from '../../i18n'
import { HOSTS_KEY, TOTP_KEY } from '../provisioning'
import { NODES_KEY } from '../Nodes'
import { newRequestId } from '../Hosts'
import { Button, Check, CodeCells, Dialog, Icon, Segmented } from '../../ui'
import { codeOf, codeText, Num, one, outcome, refusalText, releaseVersion, switchBlock, tn, type Outcome } from './nodeSwitch'
import { pushOperatorEvent } from './operatorEvents'

/*
 * Fleet update (HOLD-A8): pick nodes, then Update logic or Update agent on all of them
 * in parallel with one code. The first node's command carries the code; once the
 * center shows the confirmation (GET /api/admin/me/totp → confirmed_until) the others
 * go out without one, inside its minute — the same as scripts/nodes-update.sh. Then a
 * table of outcomes per node. Nodes that cannot take the update are listed with why
 * and are not picked.
 */

type Service = 'logic' | 'agent'
type Row = { host: Host; node?: NodeInfo; why: string; from?: string; to?: string }
type Result = { hostId: string; name: string; o: Outcome; pause?: number; generation?: number }

const CODE_TRIES = 5
const emptyCode = () => Array<string>(6).fill('')
const ready = (c: string[]) => c.every((x) => x.length === 1)
const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms))

/** The center's confirmation of the last code still holds. */
async function confirmed(): Promise<boolean> {
  try {
    const s = await hostsApi.totp()
    return !!s.confirmed_until && Date.parse(s.confirmed_until) > Date.now()
  } catch {
    return false
  }
}

export function FleetUpdateButton({ hosts, nodes, info }: { hosts: Host[]; nodes: NodeInfo[]; info?: InstallInfo }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  return (
    <>
      <Button ref={ref} className="max-md:min-h-[44px]" data-testid="fleet-open" onClick={() => setOpen(true)}>
        {t('node.fleet.open')}
      </Button>
      {open && <FleetDialog hosts={hosts} nodes={nodes} info={info} returnFocus={ref} onClose={() => setOpen(false)} />}
    </>
  )
}

function FleetDialog({ hosts, nodes, info, returnFocus, onClose }: { hosts: Host[]; nodes: NodeInfo[]; info?: InstallInfo; returnFocus: RefObject<HTMLButtonElement | null>; onClose: () => void }) {
  const qc = useQueryClient()
  const titleId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [service, setService] = useState<Service>('logic')
  const [picked, setPicked] = useState<Set<string> | null>(null)
  const [cells, setCells] = useState(emptyCode)
  const [busy, setBusy] = useState(false)
  const [tries, setTries] = useState(CODE_TRIES)
  const [codeError, setCodeError] = useState('')
  const [formError, setFormError] = useState('')
  const [results, setResults] = useState<Result[] | null>(null)

  const rows: Row[] = useMemo(
    () =>
      hosts
        .filter((h) => !h.revoked_at && h.node_id)
        .map((h) => {
          const node = nodes.find((n) => n.id === h.node_id)
          const why = !h.online ? t('node.code.offline') : h.installing ? 'An install is running on this host.' : switchBlock(service, h, info)
          const from = service === 'agent' ? h.components?.agent?.version || h.agent_version : h.components?.logic?.version
          return { host: h, node, why, from, to: releaseVersion(info, service) }
        })
        .sort((a, b) => (a.node?.name ?? a.host.slug).localeCompare(b.node?.name ?? b.host.slug)),
    [hosts, nodes, info, service],
  )
  const eligible = rows.filter((r) => !r.why)
  // Every node that can take it is picked until the operator changes the pick.
  const chosen = picked ?? new Set(eligible.map((r) => r.host.id))
  const list = eligible.filter((r) => chosen.has(r.host.id))

  useEffect(() => setPicked(null), [service])

  const closeRef = useRef(onClose)
  const backRef = useRef(returnFocus)
  closeRef.current = onClose
  backRef.current = returnFocus
  useEffect(() => {
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      backRef.current.current?.focus()
    }
  }, [])

  const toggle = (id: string, on: boolean) => {
    const s = new Set(chosen)
    if (on) s.add(id)
    else s.delete(id)
    setPicked(s)
  }

  const nameOf = (r: Row) => r.node?.name ?? r.host.slug

  const resultOf = (r: Row, c: HostCommand | undefined, e?: unknown): Result => {
    const name = nameOf(r)
    if (c) {
      // The pause has its own column.
      const o = outcome(service, { ...c, data: c.data ? { ...c.data, pause_ms: undefined } : c.data }, { name, release: r.host.release_no, streams: r.node?.streams ?? 0, version: r.to })
      const d = c.data ?? {}
      return { hostId: r.host.id, name, o, pause: typeof d.pause_ms === 'number' ? d.pause_ms : undefined, generation: typeof d.generation === 'number' ? d.generation : undefined }
    }
    const h = hostError(e)
    const head = t(service === 'logic' ? 'node.didNotRun.logic' : 'node.didNotRun.agent', { name })
    const why = refusalText(h) ?? (h?.error ? codeText(codeOf(h.error), h.error) : (e as Error)?.message ?? '')
    return { hostId: r.host.id, name, o: { state: 'fail', text: why ? `${head} ${why}` : head } }
  }

  const send = (r: Row, totp?: string) =>
    hostsApi.command(r.host.id, { kind: 'update', args: { service }, ...(totp ? { totp } : {}), request_id: newRequestId() })

  const run = async () => {
    if (!ready(cells) || busy || tries <= 0 || !list.length) return
    setBusy(true)
    setCodeError('')
    setFormError('')
    const [first, ...rest] = list
    const out = new Map<string, Result>()
    let firstDone = false
    let firstErr: unknown
    const p0 = send(first!, cells.join(''))
      .then((c) => out.set(first!.host.id, resultOf(first!, c)))
      .catch((e) => {
        firstErr = e
        out.set(first!.host.id, resultOf(first!, undefined, e))
      })
      .finally(() => {
        firstDone = true
      })
    // The others wait for the center's confirmation of the code (or the first answer).
    let ok = false
    for (let i = 0; i < 25 && !ok; i++) {
      if (firstDone) {
        ok = !firstErr
        break
      }
      ok = await confirmed()
      if (!ok) await Promise.race([p0, sleep(1000)])
    }
    const h = hostError(firstErr)
    if (h?.code === 'totp_invalid' || h?.code === 'totp_locked') {
      await p0
      const left = h.code === 'totp_locked' ? 0 : tries - 1
      setTries(left)
      setCodeError(left > 0 ? t(one(left, 'node.wrongCode.one', 'node.wrongCode.many'), { n: left }) : h.retry_after_s ? t('node.lockedFor', { n: Math.ceil(h.retry_after_s / 60) }) : t('node.locked'))
      setBusy(false)
      return
    }
    if (!ok && rest.length) {
      await p0
      setFormError(t('node.fleet.notConfirmed'))
      setResults(list.slice(0, 1).map((r) => out.get(r.host.id)!))
      setBusy(false)
      return
    }
    await Promise.all([
      p0,
      ...rest.map((r) =>
        send(r)
          .then((c) => out.set(r.host.id, resultOf(r, c)))
          .catch((e) => out.set(r.host.id, resultOf(r, undefined, e))),
      ),
    ])
    const res = list.map((r) => out.get(r.host.id)!)
    for (const x of res) {
      const r = list.find((y) => y.host.id === x.hostId)!
      pushOperatorEvent({ at: new Date().toISOString(), nodeId: r.node?.id ?? r.host.node_id ?? r.host.id, name: x.name, text: x.o.text })
    }
    setResults(res)
    setBusy(false)
    void qc.invalidateQueries({ queryKey: HOSTS_KEY })
    void qc.invalidateQueries({ queryKey: NODES_KEY })
    void qc.invalidateQueries({ queryKey: TOTP_KEY })
  }

  const okCount = results?.filter((r) => r.o.state === 'live').length ?? 0

  return (
    <Dialog
      id={titleId}
      modal
      busy={busy}
      testId="fleet-dialog"
      title={t('node.fleet.title')}
      footer={
        results ? (
          <Button ref={cancelRef} variant="default" data-testid="fleet-close" onClick={onClose}>
            {t('node.fleet.close')}
          </Button>
        ) : (
          <>
            <Button ref={cancelRef} variant="ghost" data-testid="fleet-cancel" onClick={onClose}>
              {t('node.cancel')}
            </Button>
            <Button variant="default" busy={busy} disabled={!ready(cells) || tries <= 0 || !list.length} data-testid="fleet-confirm" onClick={() => void run()}>
              {busy ? t('node.fleet.busy') : list.length === 1 ? t('node.fleet.send.one') : t('node.fleet.send.many', { n: list.length })}
            </Button>
          </>
        )
      }
    >
      {results ? (
        <div className="flex flex-col gap-2" data-testid="fleet-results">
          <p className="text-[13px] text-text-soft" data-testid="fleet-summary">
            {tn('node.fleet.done', { ok: okCount, n: results.length })}
          </p>
          <table className="sx-table compact" aria-label={t('node.fleet.results')}>
            <thead>
              <tr>
                <th>{t('node.fleet.col.node')}</th>
                <th>{t('node.fleet.col.outcome')}</th>
                <th className="num">{t('node.fleet.col.pause')}</th>
                <th className="num">{t('node.fleet.col.generation')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.hostId} data-testid={`fleet-row-${r.hostId}`} data-state={r.o.state}>
                  <td className="font-mono">{r.name}</td>
                  <td className="!whitespace-normal py-1.5">
                    <span className="flex items-start gap-1.5 text-xs">
                      <span className={r.o.state === 'live' ? 'text-live' : r.o.state === 'warn' ? 'text-warn' : 'text-fail'}>
                        <Icon name={`state-${r.o.state}`} size={12} />
                      </span>
                      <span className="text-text-soft">{r.o.text}</span>
                    </span>
                  </td>
                  <td className="num tabular dim">{r.pause !== undefined ? `${r.pause} ms` : '—'}</td>
                  <td className="num tabular dim">{r.generation ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {formError && (
            <p className="text-[13px] text-text" role="alert" data-testid="fleet-error">
              {formError}
            </p>
          )}
        </div>
      ) : (
        <>
          <Segmented
            label={t('node.fleet.what')}
            value={service}
            onChange={(v) => setService(v)}
            options={[
              { value: 'logic', label: t('node.fleet.logic'), testId: 'fleet-logic' },
              { value: 'agent', label: t('node.fleet.agent'), testId: 'fleet-agent' },
            ]}
          />
          <div className="flex flex-col gap-1" role="group" aria-label={t('node.fleet.nodes')} data-testid="fleet-nodes">
            {rows.map((r) => (
              <Check
                key={r.host.id}
                testId={`fleet-node-${r.host.id}`}
                checked={!r.why && chosen.has(r.host.id)}
                disabled={!!r.why || busy}
                onChange={(on) => toggle(r.host.id, on)}
                label={<span className="font-mono">{nameOf(r)}</span>}
                hint={
                  r.why ? (
                    r.why
                  ) : (
                    <Num>
                      {r.from ?? '—'} → {r.to ?? '—'}
                    </Num>
                  )
                }
              />
            ))}
            {!eligible.length && (
              <p className="text-xs text-muted" data-testid="fleet-none">
                {t('node.fleet.none')}
              </p>
            )}
          </div>
          <p className="text-xs text-muted">{t('node.fleet.oneCode')}</p>
          <CodeCells id="fleet-code" label={t('node.code')} value={cells} invalid={!!codeError} onChange={setCells} />
          {codeError && (
            <span className="flex items-center gap-1.5 text-xs text-fail" role="alert" data-testid="fleet-code-error">
              <Icon name="state-fail" size={12} />
              {codeError}
            </span>
          )}
          {formError && (
            <p className="text-[13px] text-text" role="alert" data-testid="fleet-error">
              {formError}
            </p>
          )}
        </>
      )}
    </Dialog>
  )
}
